import { useUser } from '../contexts/user-context/use-user';
import { OAuthConnectionInformation } from '../pages/integrations/types/types';

interface OAuthConnectionList {
  connections: Array<{
    id: string;
    provider: string;
    status: string;
    displayName?: string;
    providerAccountId?: string;
    createdAt: string;
    lastVerifiedAt?: string;
  }>;
  totalCount?: number;
  authUrl?: string;
}

export const useOAuthAPI = () => {
  const { authenticatedFetch } = useUser();

  /**
   * Check if there's an active Gmail connection for the user
   */
  const checkEmailConnection = async (): Promise<OAuthConnectionInformation> => {
    return await checkOAuthConnectionByProvider('gmail');
  };

  /**
   * Check if there's an active Xero connection for the user
   */
  const checkXeroConnection = async (): Promise<OAuthConnectionInformation> => {
    return await checkOAuthConnectionByProvider('xero');
  };

  /**
   * Check if there's an active connection for a specific provider
   */
  const checkOAuthConnectionByProvider = async (provider: string): Promise<OAuthConnectionInformation> => {
    try {
      console.debug(`Checking ${provider} connection status...`); // Debug log

      // First, check if we have any connections at all for this provider
      const connectionsList = await listOAuthConnectionsByProvider(provider);

      // If no connections exist, return early without further checks
      if (!connectionsList.connections || connectionsList.connections.length === 0) {
        console.debug(`No ${provider} connections found - skipping detailed checks`); // Debug log
        return {
          connected: false,
          error: `No ${provider} connections found`,
          authUrl: connectionsList.authUrl,
        };
      }

      // We have connections - now check if any are connected
      const connectedConnection = connectionsList.connections.find((conn) => conn.status === 'connected');

      if (connectedConnection) {
        console.debug(`Found connected ${provider} connection:`, connectedConnection); // Debug log
        return {
          connected: true,
          connectionId: connectedConnection.id,
          email: connectedConnection.displayName || connectedConnection.providerAccountId,
          lastVerifiedAt: connectedConnection.lastVerifiedAt,
        };
      } else {
        // We have connections but none are in CONNECTED status
        const connectionStatuses = connectionsList.connections.map((conn) => conn.status).join(', ');
        console.debug(`${provider} connections exist but none are connected. Statuses:`, connectionStatuses); // Debug log
        return {
          connected: false,
          error: `${provider} connections exist but none are active (statuses: ${connectionStatuses})`,
          authUrl: connectionsList.authUrl,
        };
      }
    } catch (error) {
      console.error(`Error checking ${provider} connection:`, error);
      return {
        connected: false,
        error: 'Error checking connection status',
      };
    }
  };

  /**
   * List all connections for a specific provider
   */
  const listOAuthConnectionsByProvider = async (provider: string): Promise<OAuthConnectionList> => {
    try {
      console.debug(`Fetching ${provider} connections...`); // Debug log
      const response = await authenticatedFetch(`/api/oauth/connections?provider=${provider}`);

      console.debug('Response status:', response.status); // Debug log

      if (response.ok) {
        const result = await response.json();
        console.debug(`${provider} connections result:`, result); // Debug log
        return result;
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch connections:', response.status, errorText);
        return {
          connections: [],
          authUrl: undefined,
        };
      }
    } catch (error) {
      console.error(`Error listing ${provider} connections:`, error);
      return {
        connections: [],
        authUrl: undefined,
      };
    }
  };

  return {
    checkEmailConnection,
    checkXeroConnection,
  };
};
